// ─── src/api/gewLive.ts ──────────────────────────────────────────────────────
//
// GEW Live: events held so far, door check-ins and who came, for the
// staff dashboard. One call returns everything for the current filters.

import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? "https://api.startupsyria.org/api/v1";

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { "x-api-key": process.env.REACT_APP_DASHBOARD_API_KEY ?? "" },
});

export type LivePhase = "done" | "running" | "upcoming";
export type LiveSource = "gew_event" | "meetup";

export interface LiveFilters {
  day?: string;
  event?: string;
  governorate?: string;
  type?: LiveSource;
  organizer?: string;
}

export interface LiveEvent {
  key: string;
  sourceType: LiveSource;
  id: string;
  title: string;
  organizer: string;
  governorate: string;
  city: string | null;
  eventDate: string;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  mode: string;
  capacity: number | null;
  contact: { name: string | null; phone: string | null; email: string | null };
  phase: LivePhase;
  registered: number;
  accepted: number;
  attended: number;
  showUpRate: number | null;
  femalePct: number | null;
  capacityFill: number | null;
  feedback: { emailed: number; responses: number };
  noCheckIn: boolean;
  lowCheckIn: boolean;
}

export interface DemoRow {
  label: string;
  registered: number;
  attended: number;
}

export interface RankRow {
  key: string;
  title: string;
  organizer: string;
  attended: number;
  accepted: number;
  showUpRate: number | null;
}

export interface LiveSnapshot {
  generatedAt: string;
  now: { date: string; hour: number; time: string };
  options: {
    days: string[];
    events: { key: string; title: string; organizer: string; eventDate: string; startTime: string | null }[];
    governorates: string[];
    organizers: string[];
  };
  kpis: {
    eventsTotal: number;
    eventsDone: number;
    eventsRunning: number;
    eventsUpcoming: number;
    registered: number;
    accepted: number;
    attended: number;
    uniqueAttendees: number;
    showUpRate: number | null;
    femalePct: number | null;
    noCheckIn: number;
    lowCheckIn: number;
    capacityFill: number | null;
    startedEvents: number;
  };
  days: { day: string; events: number; done: number; registered: number; attended: number }[];
  arrivals: { hour: number; count: number }[];
  demographics: { age: DemoRow[]; status: DemoRow[]; gender: DemoRow[] };
  byGovernorate: { label: string; count: number }[];
  byCity: { label: string; count: number }[];
  topByAttendance: RankRow[];
  topByShowUp: RankRow[];
  repeat: { one: number; two: number; threePlus: number };
  events: LiveEvent[];
  noCheckIn: LiveEvent[];
}

export const getLiveSnapshot = async (filters: LiveFilters) =>
  (await client.get<LiveSnapshot>("/gew-live", { params: filters })).data;
